'use client';

import { motion, useReducedMotion, useSpring, useMotionTemplate } from 'framer-motion';
import { useEffect, useState } from 'react';
import AnimatedCounter from '@/components/AnimatedCounter';
import BerryCluster from '@/components/brand/BerryCluster';
import {
  ShieldCheck,
  Zap,
  Users,
  Sparkles,
  AlertCircle,
  X,
  Check,
  FileSpreadsheet,
  Database,
  ArrowRight,
  TrendingUp,
  Clock,
  Activity,
} from 'lucide-react';
import Link from 'next/link';

const EASE = [0.16, 1, 0.3, 1] as const;


export default function WhyBlueberries() {
  return (
    <section id="why" className="relative py-20 md:py-28 overflow-hidden" style={{ backgroundColor: '#FBFAFF' }}>

      {/* Subtle grid texture only — top-anchored bloom removed so backgrounds line up between sections */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(76,29,149,1) 1px, transparent 1px), linear-gradient(90deg, rgba(76,29,149,1) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />

      <div className="relative container mx-auto max-w-7xl px-6">
        {/* ─── Section header ─────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-purple-700/80 font-semibold">
            <span className="h-px w-8 bg-purple-300" />
            Why Blueberries
            <span className="h-px w-8 bg-purple-300" />
          </div>
          <h2 className="mt-6 text-4xl md:text-6xl font-semibold tracking-[-0.035em] text-gray-900 leading-[1.02]">
            From spreadsheet chaos <br className="hidden md:block" />
            to{' '}
            <span className="bg-gradient-to-br from-[#6D28D9] via-[#7C3AED] to-[#A78BFA] bg-clip-text text-transparent">
              finance clarity.
            </span>
          </h2>
          <p className="mt-7 text-lg md:text-xl text-gray-600 leading-[1.6]">
            Most FP&amp;A teams still run on 2010 tooling: manual, scattered, and slow.{' '}
            <span className="font-semibold text-gray-900">Blueberries changes that.</span>
          </p>
        </motion.div>

        {/* ═══════════════════ ACT 1 · Before / After ═══════════════════ */}
        <div className="mt-20 md:mt-24 grid lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-4 items-stretch">
          <BeforePanel />
          <Connector />
          <AfterPanel />
        </div>

        {/* ═══════════════════ ACT 2 · Outcome story ═══════════════════ */}
        <div className="mt-32 md:mt-44">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: EASE }}
            className="text-center mb-20 md:mb-28 max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-purple-700/80 font-semibold">
              <span className="h-px w-8 bg-purple-300" />
              What changes
              <span className="h-px w-8 bg-purple-300" />
            </div>
            <h3 className="mt-6 text-4xl md:text-6xl font-semibold tracking-[-0.035em] text-gray-900 leading-[1.02]">
              Three outcomes finance teams feel{' '}
              <span className="italic font-light text-gray-400">immediately.</span>
            </h3>
          </motion.div>

          {/* Three full-width story rows */}
          <div className="space-y-32 md:space-y-44">
            <OutcomeRow
              n="01"
              icon={ShieldCheck}
              title="Trust the numbers."
              stat="96.8"
              statSuffix="%"
              statLabel="Forecast accuracy"
              kicker="Audit-grade"
              desc="Every figure traces back to source. Driver-based math you can defend in a board meeting, without opening a single spreadsheet."
              bullets={[
                'Every cell has a lineage',
                'Variance auto-flagged in real time',
                'SOC 2 Type II controls, audit in progress',
              ]}
              visual={<TrustVisual />}
              flip={false}
            />
            <OutcomeRow
              n="02"
              icon={Zap}
              title="Move at business speed."
              stat="2"
              statSuffix="s"
              statLabel="From data to insight"
              kicker="Always live"
              desc="Forecasts update the moment your business does. No more 30-day cycles. No more month-end scrambles. Real-time, every minute, every metric."
              bullets={[
                'Live recalc on every data change',
                'No batch jobs. No nightly ETL.',
                'Anomalies surfaced before month-end',
              ]}
              visual={<SpeedVisual />}
              flip={true}
            />
            <OutcomeRow
              n="03"
              icon={Users}
              title="Free your finance team."
              stat="0"
              statSuffix=" hrs"
              statLabel="Manual data work"
              kicker="Strategic time back"
              desc="AI handles the plumbing: collection, enrichment, allocation. Your CFO and team finally spend their week on strategy, not data janitorial."
              bullets={[
                '72 hrs back per CFO / week',
                'Zero manual reconciliation',
                'AI agents instead of analysts',
              ]}
              visual={<FreedomVisual />}
              flip={false}
            />
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-28 flex items-center justify-center gap-3 text-sm text-gray-500"
          >
            <span>See how it works in 60 seconds.</span>
            <Link
              href="/products"
              className="inline-flex items-center gap-2 text-purple-700 hover:text-purple-900 font-medium transition-colors group"
            >
              Watch the demo
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// BEFORE PANEL — visualizes the chaos
// ═══════════════════════════════════════════════════════════════════════════

function BeforePanel() {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={{ opacity: 0, x: -30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.8, ease: EASE }}
      className="relative rounded-3xl border border-red-100/70 bg-gradient-to-br from-red-50/40 via-white to-orange-50/30 p-7 md:p-9 overflow-hidden min-h-[480px]"
    >
      {/* Background noise — chaotic vibe */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Eyebrow — centered block */}
      <div className="relative flex flex-col items-center text-center mb-6 gap-2">
        <span className="inline-flex items-center gap-2.5 text-[13px] md:text-[14px] tracking-[0.28em] uppercase text-red-600 font-bold">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
          Without Blueberries
        </span>
        <span className="text-[10px] tracking-[0.2em] uppercase text-red-400 font-medium">Chaos · slow · manual</span>
      </div>

      {/* Headline */}
      <h3 className="relative text-2xl md:text-3xl font-semibold tracking-tight text-gray-900 leading-tight">
        Spreadsheets. Everywhere.
      </h3>
      <p className="relative mt-2 text-[14px] text-gray-500 leading-relaxed max-w-sm">
        Your forecast lives in 47 tabs across 12 files. Nobody&apos;s sure which is the latest.
      </p>

      {/* Visual chaos */}
      <div className="relative mt-8 h-[200px]">
        {/* Stacked tilted file thumbnails */}
        {[
          { name: 'Budget_FINAL_v14.xlsx', rotate: -8, x: 0,   y: 0,  delay: 0.1 },
          { name: 'Q3_forecast_REAL.xlsx', rotate: 4,  x: 40,  y: 15, delay: 0.2 },
          { name: 'Variance_FY24_v2.xlsx', rotate: -3, x: 80,  y: 5,  delay: 0.3 },
          { name: 'Allocations_DRAFT.xlsx', rotate: 7, x: 130, y: 20, delay: 0.4 },
        ].map((f, i) => (
          <motion.div
            key={f.name}
            initial={{ opacity: 0, y: 20, rotate: f.rotate * 1.5 }}
            whileInView={{ opacity: 1, y: f.y, rotate: f.rotate }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, ease: EASE, delay: f.delay }}
            style={{ left: f.x }}
            className="absolute top-0 w-[170px] h-[110px]"
          >
            {/* Inner layer wobbles continuously — the chaos never settles */}
            <motion.div
              animate={reduce ? undefined : { rotate: [0, 0.8, 0, -0.8, 0], y: [0, -2.5, 0, 2.5, 0] }}
              transition={reduce ? undefined : { duration: 5.5 + i * 0.7, repeat: Infinity, ease: 'easeInOut' }}
              className="h-full w-full rounded-lg border border-gray-200 bg-white p-2.5 shadow-[0_8px_20px_-6px_rgba(0,0,0,0.08)]"
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <FileSpreadsheet className="h-3 w-3 text-emerald-600" />
                <span className="truncate text-[9px] font-medium text-gray-700">{f.name}</span>
              </div>
              {/* Fake spreadsheet rows */}
              <div className="grid grid-cols-3 gap-px bg-gray-100 rounded overflow-hidden">
                {Array.from({ length: 12 }).map((_, k) => (
                  <div key={k} className={`h-2.5 ${k % 4 === 0 ? 'bg-emerald-50' : 'bg-white'}`} />
                ))}
              </div>
            </motion.div>
            {i === 0 && (
              <motion.span
                animate={reduce ? undefined : { opacity: [1, 0.45, 1] }}
                transition={reduce ? undefined : { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-1.5 -right-1.5 inline-flex items-center gap-1 rounded-full bg-red-100 px-1.5 py-0.5 text-[8px] font-bold text-red-700"
              >
                <AlertCircle className="h-2.5 w-2.5" />
                Stale
              </motion.span>
            )}
          </motion.div>
        ))}

        {/* Floating "broken connection" icons */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="absolute right-0 top-3 flex flex-col gap-2"
        >
          <div className="inline-flex items-center gap-1.5 rounded-lg border border-red-200/60 bg-white px-2 py-1.5 shadow-sm">
            <Database className="h-3 w-3 text-gray-400" />
            <X className="h-3 w-3 text-red-500" />
            <span className="text-[9px] font-medium text-gray-500">NetSuite</span>
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-lg border border-red-200/60 bg-white px-2 py-1.5 shadow-sm">
            <Database className="h-3 w-3 text-gray-400" />
            <X className="h-3 w-3 text-red-500" />
            <span className="text-[9px] font-medium text-gray-500">AppFolio</span>
          </div>
        </motion.div>

        {/* Glitchy error toast — flickers like a real broken formula */}
        <motion.div
          animate={reduce ? undefined : { opacity: [0, 1, 1, 0.3, 1], x: [0, -1, 1, 0] }}
          transition={reduce ? undefined : { duration: 3, repeat: Infinity, ease: 'easeInOut', times: [0, 0.1, 0.6, 0.7, 0.8] }}
          className="absolute bottom-0 right-2 inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 shadow-[0_8px_20px_-8px_rgba(239,68,68,0.5)]"
        >
          <AlertCircle className="h-3 w-3 text-red-500" />
          <span className="font-mono text-[9px] font-bold text-red-600">#REF! · 3 conflicts</span>
        </motion.div>
      </div>

      {/* Pain stats */}
      <div className="relative mt-8 grid grid-cols-2 gap-3">
        <PainStat icon={<Clock className="h-3.5 w-3.5" />} value="80 hrs/wk" label="manual work" />
        <PainStat icon={<AlertCircle className="h-3.5 w-3.5" />} value="14 days" label="stale data" />
      </div>
    </motion.div>
  );
}

function PainStat({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-red-100/60 bg-white/70 px-3 py-2 backdrop-blur">
      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-50 text-red-600">{icon}</span>
      <div>
        <div className="text-sm font-semibold text-gray-900 tabular-nums">{value}</div>
        <div className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">{label}</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTRAST STAT — bad → good, the payoff made unmissable
// ═══════════════════════════════════════════════════════════════════════════

function ContrastStat({ from, to, label }: { from: string; to: string; label: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, ease: EASE }}
      className="flex items-center justify-center gap-4 rounded-2xl border border-purple-100/70 bg-white px-5 py-4 shadow-[0_24px_60px_-30px_rgba(76,29,149,0.35)]"
    >
      <span className="text-2xl md:text-[32px] font-semibold text-red-300 line-through decoration-red-300/50 tabular-nums leading-none">
        {from}
      </span>
      <motion.span
        initial={{ x: -6, opacity: 0 }}
        whileInView={{ x: 0, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.25, duration: 0.45, ease: EASE }}
        className="shrink-0"
      >
        <ArrowRight className="h-6 w-6 text-purple-500" strokeWidth={2.4} />
      </motion.span>
      <span className="bg-gradient-to-br from-[#4C1D95] via-[#6D28D9] to-[#A78BFA] bg-clip-text text-2xl md:text-[32px] font-semibold leading-none tabular-nums text-transparent">
        {to}
      </span>
      <span className="ml-1 hidden text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400 md:block">
        {label}
      </span>
    </motion.div>
  );
}

// 3D cursor-tilt wrapper with a moving glare — premium, tactile depth
function TiltCard({ children }: { children: React.ReactNode }) {
  const reduce = useReducedMotion();
  const rx = useSpring(0, { stiffness: 150, damping: 18 });
  const ry = useSpring(0, { stiffness: 150, damping: 18 });
  const gx = useSpring(50, { stiffness: 120, damping: 20 });
  const gy = useSpring(50, { stiffness: 120, damping: 20 });
  const glare = useMotionTemplate`radial-gradient(440px circle at ${gx}% ${gy}%, rgba(196,181,253,0.22), transparent 56%)`;

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top) / r.height - 0.5;
    ry.set(nx * 8);
    rx.set(-ny * 8);
    gx.set(50 + nx * 60);
    gy.set(50 + ny * 60);
  };
  const onLeave = () => { rx.set(0); ry.set(0); gx.set(50); gy.set(50); };

  return (
    <div className="[perspective:1200px]" onMouseMove={onMove} onMouseLeave={onLeave}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: 'preserve-3d' }}
        className="relative"
      >
        {children}
        {!reduce && (
          <motion.div aria-hidden style={{ background: glare }} className="pointer-events-none absolute inset-0 z-20 rounded-3xl" />
        )}
      </motion.div>
    </div>
  );
}

// Live-ticking revenue figure for the "After" mini dashboard
function LiveRevenueMini() {
  const reduce = useReducedMotion();
  const [v, setV] = useState(8.43);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (reduce) return;
    const iv = setInterval(() => {
      setV((p) => {
        let n = p + (Math.random() - 0.45) * 0.02;
        if (n < 8.4) n = 8.4;
        if (n > 8.47) n = 8.47;
        return +n.toFixed(2);
      });
      setFlash(true);
      window.setTimeout(() => setFlash(false), 500);
    }, 2500);
    return () => clearInterval(iv);
  }, [reduce]);
  return <span className={`transition-colors duration-300 ${flash ? 'text-purple-600' : ''}`}>${v.toFixed(2)}M</span>;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONNECTOR — arrow between before and after
// ═══════════════════════════════════════════════════════════════════════════

function Connector() {
  const reduce = useReducedMotion();
  return (
    <div className="relative hidden lg:flex flex-col items-center justify-center px-3">
      {/* Single continuous hairline — fades in at the ends, solid in the middle */}
      <div
        aria-hidden
        className="relative h-full min-h-[280px] w-px overflow-hidden"
        style={{
          background:
            'linear-gradient(to bottom, transparent 0%, rgba(167,139,250,0.55) 18%, rgba(124,58,237,0.7) 50%, rgba(167,139,250,0.55) 82%, transparent 100%)',
        }}
      >
        {/* Particles streaming down the line — the simple effect that was working */}
        {!reduce && Array.from({ length: 3 }).map((_, i) => (
          <motion.span
            key={i}
            className="absolute left-1/2 -translate-x-1/2 h-2 w-2 rounded-full bg-purple-500 shadow-[0_0_10px_rgba(124,58,237,0.7)]"
            initial={{ top: '0%', opacity: 0 }}
            animate={{ top: ['0%', '100%'], opacity: [0, 1, 1, 0] }}
            transition={{
              duration: 2.6,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.85,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// AFTER PANEL — visualizes the clarity
// ═══════════════════════════════════════════════════════════════════════════

function AfterPanel() {
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
      className="relative rounded-3xl border border-purple-100/80 bg-gradient-to-br from-purple-50/40 via-white to-violet-50/30 p-7 md:p-9 overflow-hidden min-h-[480px] shadow-[0_30px_80px_-30px_rgba(76,29,149,0.4)]"
    >
      {/* Subtle accent glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-1 opacity-60"
        style={{ background: 'radial-gradient(50% 60% at 50% 0%, rgba(167,139,250,0.25), transparent 70%)' }}
      />
      {/* Top gradient line */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #A78BFA, transparent)' }} />

      {/* Eyebrow — centered block */}
      <div className="relative flex flex-col items-center text-center mb-6 gap-2">
        <span className="inline-flex items-center gap-2.5 text-[13px] md:text-[14px] tracking-[0.28em] uppercase text-purple-700 font-bold">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          With Blueberries
        </span>
        <span className="text-[10px] tracking-[0.2em] uppercase text-purple-500/70 font-medium">Clarity · live · automated</span>
      </div>

      {/* Headline */}
      <h3 className="relative text-2xl md:text-3xl font-semibold tracking-tight text-gray-900 leading-tight">
        One source.{' '}
        <span className="bg-gradient-to-r from-[#6D28D9] to-[#A78BFA] bg-clip-text text-transparent">Always fresh.</span>
      </h3>
      <p className="relative mt-2 text-[14px] text-gray-500 leading-relaxed max-w-sm">
        Every tool, every number, in one place. Updated continuously. Trusted by default.
      </p>

      {/* Visual clarity — mini dashboard */}
      <div className="relative mt-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ delay: 0.3, duration: 0.7, ease: EASE }}
          className="relative rounded-2xl border border-purple-100/80 bg-white p-3.5 shadow-[0_12px_36px_-12px_rgba(76,29,149,0.25)]"
        >
          {/* Mock top bar */}
          <div className="flex items-center justify-between border-b border-gray-100 pb-2.5 mb-3">
            <div className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-gray-200" />
              <span className="h-1.5 w-1.5 rounded-full bg-gray-200" />
              <span className="h-1.5 w-1.5 rounded-full bg-gray-200" />
            </div>
            <span className="text-[9px] tracking-wider text-gray-400 font-medium">blueberries.app</span>
            <span className="flex items-center gap-1 text-[9px] font-medium text-emerald-600">
              <span className="h-1 w-1 rounded-full bg-emerald-500 animate-pulse" />
              Live · 2s ago
            </span>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[
              { l: 'Revenue', v: '$8.43M', c: 'text-gray-900' },
              { l: 'Capture', v: '94%',    c: 'text-purple-700' },
              { l: 'Accuracy', v: '96.8%', c: 'text-emerald-600' },
            ].map((k) => (
              <div key={k.l} className="rounded-lg border border-gray-100 px-2 py-1.5">
                <div className="text-[8px] uppercase tracking-wider text-gray-400 font-medium">{k.l}</div>
                <div className={`text-sm font-semibold ${k.c} tabular-nums`}>
                  {k.l === 'Revenue' ? <LiveRevenueMini /> : k.v}
                </div>
              </div>
            ))}
          </div>

          {/* Mini sparkline */}
          <svg viewBox="0 0 280 50" className="h-10 w-full">
            <defs>
              <linearGradient id="aftFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#A78BFA" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#A78BFA" stopOpacity="0" />
              </linearGradient>
            </defs>
            <motion.path
              d="M0,40 C30,34 60,18 100,22 C140,26 170,8 210,10 C250,12 270,6 280,4 L280,50 L0,50 Z"
              fill="url(#aftFill)"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.5 }}
            />
            <motion.path
              d="M0,40 C30,34 60,18 100,22 C140,26 170,8 210,10 C250,12 270,6 280,4"
              fill="none"
              stroke="url(#aftLine)"
              strokeWidth="2"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 1.1, ease: EASE }}
            />
            <defs>
              <linearGradient id="aftLine" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#7C3AED" />
                <stop offset="100%" stopColor="#6D28D9" />
              </linearGradient>
            </defs>
          </svg>
        </motion.div>

        {/* Connected source pills */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-3 flex flex-wrap gap-1.5"
        >
          {['NetSuite', 'AppFolio', 'Gusto', 'Stripe', 'QuickBooks'].map((name, i) => (
            <span
              key={name}
              className="inline-flex items-center gap-1 rounded-md border border-purple-100/70 bg-white/80 px-2 py-1 text-[9px] font-medium text-gray-700 backdrop-blur"
            >
              <Check className="h-2.5 w-2.5 text-emerald-500" strokeWidth={3} />
              {name}
            </span>
          ))}
          <span className="inline-flex items-center gap-1 rounded-md bg-purple-50 px-2 py-1 text-[9px] font-semibold text-purple-700">
            +9 more
          </span>
        </motion.div>
      </div>

      {/* Win stats */}
      <div className="relative mt-7 grid grid-cols-2 gap-3">
        <WinStat icon={<Zap className="h-3.5 w-3.5" />} value="0 hrs" label="manual work" />
        <WinStat icon={<Activity className="h-3.5 w-3.5" />} value="Real-time" label="data freshness" />
      </div>
    </motion.div>
  );
}

function WinStat({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-purple-100/70 bg-white/80 px-3 py-2 backdrop-blur">
      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-50 text-purple-700">{icon}</span>
      <div>
        <div className="text-sm font-semibold text-gray-900 tabular-nums">{value}</div>
        <div className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">{label}</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// OUTCOME ROW — full-width story sequence (Stripe/Linear style)
// ═══════════════════════════════════════════════════════════════════════════

function OutcomeRow({
  n,
  icon: Icon,
  title,
  stat,
  statSuffix,
  statLabel,
  kicker,
  desc,
  bullets,
  visual,
  flip,
}: {
  n: string;
  icon: React.ElementType;
  title: string;
  stat: string;
  statSuffix: string;
  statLabel: string;
  kicker: string;
  desc: string;
  bullets: string[];
  visual: React.ReactNode;
  flip: boolean;
}) {
  const reduce = useReducedMotion();

  const statValue = parseFloat(stat);
  const statDecimals = stat.includes('.') ? 1 : 0;

  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-120px' }}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
      }}
      className="relative grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
    >
      {/* Oversized ghost numeral */}
      <span
        aria-hidden
        className={`pointer-events-none absolute -z-10 select-none text-[260px] md:text-[400px] font-bold leading-none tracking-tighter text-purple-50 ${
          flip ? 'right-0 lg:-right-10' : 'left-0 lg:-left-10'
        } -top-20`}
      >
        {n}
      </span>
      {/* ── Text side ───────────────────────────────────────────── */}
      <motion.div
        variants={{
          hidden: { opacity: 0, x: flip ? 30 : -30 },
          show:   { opacity: 1, x: 0, transition: { duration: 0.8, ease: EASE } },
        }}
        className={flip ? 'lg:order-2' : ''}
      >
        {/* Eyebrow row: number + icon + kicker */}
        <div className="flex items-center gap-3 mb-6">
          <span className="text-[11px] tracking-[0.32em] uppercase text-purple-700/80 font-bold tabular-nums">
            {n}
          </span>
          <span className="h-px w-10 bg-purple-200" />
          <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase text-gray-500 font-semibold">
            <Icon className="h-3.5 w-3.5 text-purple-600" strokeWidth={2.2} />
            {kicker}
          </span>
        </div>

        <h3 className="text-[40px] md:text-[60px] font-semibold tracking-[-0.04em] text-gray-900 leading-[0.95]">
          {title}
        </h3>

        {/* Massive stat — counts up in view */}
        <div className="mt-8 md:mt-10 flex items-baseline gap-1">
          <AnimatedCounter
            value={statValue}
            decimals={statDecimals}
            durationMs={1500}
            className="text-[88px] md:text-[120px] font-semibold tracking-[-0.05em] leading-none bg-gradient-to-br from-[#4C1D95] via-[#6D28D9] to-[#A78BFA] bg-clip-text text-transparent tabular-nums"
          />
          <span className="text-[40px] md:text-[56px] font-medium text-gray-300 tabular-nums leading-none">
            {statSuffix}
          </span>
        </div>
        <div className="mt-3 text-[11px] tracking-[0.28em] uppercase text-gray-400 font-semibold">
          {statLabel}
        </div>

        <p className="mt-8 text-[17px] md:text-[18px] text-gray-600 leading-[1.6] max-w-md">{desc}</p>

        <ul className="mt-7 space-y-3">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-purple-100">
                <Check className="h-3 w-3 text-purple-700" strokeWidth={3} />
              </span>
              <span className="text-[15px] text-gray-700">{b}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      {/* ── Visual side ─────────────────────────────────────────── */}
      <motion.div
        variants={{
          hidden: { opacity: 0, x: flip ? -40 : 40, scale: 0.96 },
          show:   { opacity: 1, x: 0, scale: 1, transition: { duration: 0.9, ease: EASE } },
        }}
        className={`relative ${flip ? 'lg:order-1' : ''}`}
      >
        <TiltCard>{visual}</TiltCard>
      </motion.div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// VISUAL 1 — Trust: forecast vs actual with confidence band
// ═══════════════════════════════════════════════════════════════════════════

function TrustVisual() {
  return (
    <div className="relative rounded-3xl border border-purple-100/80 bg-white p-6 md:p-8 shadow-[0_30px_80px_-30px_rgba(76,29,149,0.35)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-gray-400 font-semibold">Forecast vs Actual</div>
          <div className="mt-0.5 text-sm font-semibold text-gray-900">Trailing 12 months</div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          96.8% match
        </span>
      </div>

      {/* Chart */}
      <svg viewBox="0 0 480 220" className="w-full h-auto">
        <defs>
          <linearGradient id="trustBand" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#A78BFA" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#A78BFA" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="trustLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="100%" stopColor="#6D28D9" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {[40, 90, 140, 190].map((y) => (
          <line key={y} x1="0" y1={y} x2="480" y2={y} stroke="#F3F4F6" strokeWidth="1" />
        ))}

        {/* Confidence band */}
        <motion.path
          d="M0,80 C40,72 80,60 130,52 C180,44 220,56 270,42 C320,28 360,36 420,22 C440,18 460,16 480,12 L480,40 C460,46 440,42 420,52 C360,68 320,60 270,72 C220,84 180,72 130,80 C80,88 40,98 0,108 Z"
          fill="url(#trustBand)"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.4 }}
        />

        {/* Forecast (dashed) */}
        <motion.path
          d="M0,94 C40,86 80,74 130,66 C180,58 220,68 270,57 C320,46 360,48 420,37 C440,33 460,29 480,26"
          fill="none"
          stroke="#A78BFA"
          strokeWidth="2"
          strokeDasharray="5 4"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.3, ease: EASE, delay: 0.3 }}
        />
        {/* Actual (solid) */}
        <motion.path
          d="M0,96 C40,90 80,80 130,68 C180,56 220,70 270,60 C320,50 360,50 420,40 C440,36 460,32 480,28"
          fill="none"
          stroke="url(#trustLine)"
          strokeWidth="3"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.4 }}
        />
        {/* Endpoint */}
        <motion.circle
          cx={480}
          cy={28}
          r={5}
          fill="#6D28D9"
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1.7, type: 'spring', stiffness: 200 }}
        />

        {/* Legend */}
        <g transform="translate(8,206)">
          <rect x="0" y="-6" width="14" height="2" rx="1" fill="url(#trustLine)" />
          <text x="20" y="-2" className="text-[10px]" fill="#6B7280" style={{ fontSize: '10px', fontWeight: 600 }}>Actual</text>
          <line x1="80" y1="-5" x2="94" y2="-5" stroke="#A78BFA" strokeWidth="2" strokeDasharray="4 3" />
          <text x="100" y="-2" className="text-[10px]" fill="#9CA3AF" style={{ fontSize: '10px', fontWeight: 500 }}>Forecast</text>
        </g>
      </svg>

      {/* Footer chips */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="rounded-xl border border-gray-100 px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-gray-400 font-semibold">Sources</div>
          <div className="text-sm font-semibold text-gray-900 tabular-nums">1,247</div>
        </div>
        <div className="rounded-xl border border-gray-100 px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-gray-400 font-semibold">Lineage</div>
          <div className="text-sm font-semibold text-gray-900">100%</div>
        </div>
        <div className="rounded-xl border border-purple-100 bg-purple-50/50 px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-purple-600 font-semibold">vs Legacy</div>
          <div className="text-sm font-semibold text-purple-700">+24%</div>
        </div>
      </div>

      {/* Floating audit chip */}
      <motion.div
        initial={{ opacity: 0, y: 8, scale: 0.9 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 1.2, duration: 0.5, ease: EASE }}
        className="absolute -top-3 -right-3 inline-flex items-center gap-1.5 rounded-xl border border-purple-100 bg-white px-3 py-1.5 shadow-[0_10px_28px_-10px_rgba(76,29,149,0.4)]"
      >
        <ShieldCheck className="h-3.5 w-3.5 text-purple-600" />
        <span className="text-[10px] font-semibold text-gray-700">Audit-grade</span>
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// VISUAL 2 — Speed: legacy timeline vs Blueberries instant
// ═══════════════════════════════════════════════════════════════════════════

function SpeedVisual() {
  return (
    <div className="relative rounded-3xl border border-purple-100/80 bg-white p-6 md:p-8 shadow-[0_30px_80px_-30px_rgba(76,29,149,0.35)] overflow-hidden">
      <div className="text-[10px] tracking-[0.22em] uppercase text-gray-400 font-semibold mb-7">
        Data → insight · the race
      </div>

      {/* Legacy stack */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[12px] font-semibold text-gray-500">Legacy stack</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-600 tabular-nums">
            <Clock className="h-3 w-3" />
            30 days
          </span>
        </div>
        <div className="relative h-2.5 rounded-full bg-gray-100 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: '100%' }}
            viewport={{ once: true }}
            transition={{ duration: 1.8, ease: 'easeInOut', delay: 0.3 }}
            className="h-full rounded-full"
            style={{ background: 'linear-gradient(90deg, #FECACA, #FCA5A5, #F87171)' }}
          />
        </div>
        <div className="mt-2.5 grid grid-cols-4 gap-1 text-[9px] text-gray-400 font-medium">
          <span>Collect · 7d</span>
          <span>Clean · 10d</span>
          <span>Model · 8d</span>
          <span className="text-right">Present · 5d</span>
        </div>
      </div>

      {/* Blueberries */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[12px] font-semibold text-purple-700">Blueberries</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-bold text-purple-700 tabular-nums">
            <Zap className="h-3 w-3" />
            2 seconds
          </span>
        </div>
        <div className="relative h-2.5 rounded-full bg-gray-100 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: '0.8%' }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: 0.5 }}
            className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#A78BFA] shadow-[0_0_10px_rgba(124,58,237,0.7)]"
          />
        </div>
        <div className="mt-2.5 flex items-center gap-1.5 text-[10px] text-purple-600 font-semibold">
          <Activity className="h-3 w-3" />
          Live · every minute · zero batch jobs
        </div>
      </div>

      {/* Big speedup callout */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 1.1, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
        className="mt-9 flex items-center justify-between rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 to-violet-50 px-5 py-4"
      >
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-purple-700/80 font-semibold">Speedup factor</div>
          <div className="mt-0.5 text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight tabular-nums">
            1,296,000<span className="text-lg font-medium text-purple-600 ml-1">×</span>
          </div>
          <div className="mt-0.5 text-[10px] text-gray-500">vs traditional FP&amp;A cycles</div>
        </div>
        <Zap className="h-10 w-10 text-purple-600" strokeWidth={1.5} />
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// VISUAL 3 — Freedom: time allocation before/after
// ═══════════════════════════════════════════════════════════════════════════

function FreedomVisual() {
  return (
    <div className="relative rounded-3xl border border-purple-100/80 bg-white p-6 md:p-8 shadow-[0_30px_80px_-30px_rgba(76,29,149,0.35)] overflow-hidden">
      <div className="flex items-center justify-between mb-7">
        <div className="text-[10px] tracking-[0.22em] uppercase text-gray-400 font-semibold">Where finance time goes</div>
        <span className="text-[10px] text-gray-400">per 40-hr week</span>
      </div>

      {/* Two donuts */}
      <div className="grid grid-cols-2 gap-6">
        <DonutCard label="Before" amount="80%" sub="Data plumbing" pct={80} color="#EF4444" trackColor="#FEE2E2" delay={0.3} />
        <DonutCard label="After"  amount="75%" sub="Strategic work" pct={75} color="#7C3AED" trackColor="#EDE9FE" delay={0.6} accent />
      </div>

      {/* Arrow between */}
      <div className="absolute left-1/2 top-[44%] -translate-x-1/2 -translate-y-1/2 hidden md:block z-10">
        <motion.div
          initial={{ opacity: 0, x: -8 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 1.1, duration: 0.5 }}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white border border-purple-100 shadow-[0_8px_24px_-8px_rgba(76,29,149,0.4)]"
        >
          <ArrowRight className="h-4 w-4 text-purple-700" strokeWidth={2.5} />
        </motion.div>
      </div>

      {/* Hours-back callout */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 1.2, duration: 0.5 }}
        className="mt-7 flex items-center justify-between rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 to-violet-50 px-5 py-4"
      >
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-purple-700/80 font-semibold">Hours back</div>
          <div className="mt-0.5 text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight tabular-nums">
            +72<span className="text-lg font-medium text-purple-600 ml-1">hrs / week</span>
          </div>
          <div className="mt-0.5 text-[10px] text-gray-500">per CFO &amp; finance team</div>
        </div>
        <Sparkles className="h-10 w-10 text-purple-600" strokeWidth={1.5} />
      </motion.div>
    </div>
  );
}

function DonutCard({ label, amount, sub, pct, color, trackColor, delay, accent }: {
  label: string; amount: string; sub: string; pct: number;
  color: string; trackColor: string; delay: number; accent?: boolean;
}) {
  const R = 38;
  const C = 2 * Math.PI * R;
  return (
    <div className={`rounded-2xl border px-3 py-4 text-center ${accent ? 'border-purple-100 bg-purple-50/40' : 'border-gray-100 bg-white'}`}>
      <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${accent ? 'text-purple-700' : 'text-gray-400'}`}>
        {label}
      </div>
      <div className="relative mx-auto mt-3 h-28 w-28">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle cx="50" cy="50" r={R} fill="none" stroke={trackColor} strokeWidth="12" />
          <motion.circle
            cx="50" cy="50" r={R} fill="none"
            stroke={color} strokeWidth="12" strokeLinecap="round"
            strokeDasharray={C}
            initial={{ strokeDashoffset: C }}
            whileInView={{ strokeDashoffset: C * (1 - pct / 100) }}
            viewport={{ once: true }}
            transition={{ duration: 1.3, ease: EASE, delay }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-2xl font-semibold tabular-nums ${accent ? 'text-purple-700' : 'text-gray-900'}`}>
            {amount}
          </span>
        </div>
      </div>
      <div className="mt-2 text-[11px] text-gray-600 font-medium">{sub}</div>
    </div>
  );
}
